import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Calculator, Loader2, Trash2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

const SCENARIO_COLOR = { bear: "#EF4444", base: "#D4AF37", bull: "#22C55E" };

const fmtMoney = (n) => {
  if (!n && n !== 0) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

export default function Finance() {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "FY26 Plan", revenue_base: 850000, cost_base: 520000,
    growth_pct: 0.18, horizon_months: 12, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get("/finance/scenarios");
    setScenarios(r.data);
    if (!active) {
      const populated = r.data.find((s) => s.scenarios?.base);
      if (populated) setActive(populated);
    }
  };
  useEffect(() => { refresh(); }, []);

  const run = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/finance/model", form);
      setActive(r.data); refresh();
      setOpen(false);
      toast.success("Scenario model executed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete scenario?")) return;
    await api.delete(`/finance/scenarios/${id}`);
    setActive(null); refresh();
  };

  // Chart data
  const chartData = active?.scenarios?.base?.series?.map((row, i) => ({
    month: row.month,
    bear: active.scenarios.bear.series[i]?.ebitda,
    base: row.ebitda,
    bull: active.scenarios.bull.series[i]?.ebitda,
  })) || [];

  return (
    <div className="p-8 space-y-6" data-testid="finance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Financial Intelligence</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Scenario Modeling</h1>
          <p className="text-slate-400 mt-2 text-sm">Bear / base / bull projections with AI strategic commentary.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-scenario-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Plus className="w-4 h-4" /> Run Scenario
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-3 space-y-2">
          <div className="label-eyebrow mb-2">// Scenarios ({scenarios.length})</div>
          {scenarios.map((s) => (
            <motion.div
              layout key={s.id}
              onClick={() => setActive(s)}
              data-testid={`scenario-${s.id}`}
              className={`p-4 cursor-pointer border ${active?.id === s.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
                <span className="label-eyebrow">{s.scenarios?.base ? "modeled" : "draft"}</span>
              </div>
              <div className="font-display text-sm text-white">{s.name}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-1">
                {s.inputs?.horizon_months || 12}m · {((s.inputs?.growth_pct || 0) * 100).toFixed(0)}% growth
              </div>
            </motion.div>
          ))}
        </div>

        <div className="col-span-12 lg:col-span-9 space-y-4">
          {active && active.scenarios?.base ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                {["bear", "base", "bull"].map((k) => {
                  const sc = active.scenarios[k];
                  return (
                    <div key={k} className="nexus-card p-5" data-testid={`scenario-card-${k}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="label-eyebrow" style={{ color: SCENARIO_COLOR[k] }}>{k}</span>
                        <span className="font-mono text-[10px] text-slate-500">{sc.growth_pct_annual.toFixed(1)}%</span>
                      </div>
                      <div className="font-mono text-2xl text-white">{fmtMoney(sc.total_ebitda)}</div>
                      <div className="text-[11px] text-slate-500 mt-1">Total EBITDA · {active.inputs.horizon_months}m</div>
                      <div className="mt-2 font-mono text-xs text-slate-400">Rev {fmtMoney(sc.total_revenue)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Chart */}
              <div className="nexus-card p-6" data-testid="finance-chart">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-eyebrow mb-1">// EBITDA trajectory</div>
                    <h3 className="font-display text-lg text-white">{active.name}</h3>
                  </div>
                  <button onClick={() => remove(active.id)} className="p-2 border border-white/10 text-slate-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%" minHeight={250}>
                    <LineChart data={chartData}>
                      <XAxis dataKey="month" axisLine={false} tickLine={false} tickFormatter={(m) => `M${m}`} />
                      <YAxis axisLine={false} tickLine={false} tickFormatter={fmtMoney} />
                      <Tooltip
                        contentStyle={{ background: "#0A111F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                        formatter={fmtMoney}
                      />
                      <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em" }} />
                      <Line type="monotone" dataKey="bear" stroke={SCENARIO_COLOR.bear} strokeWidth={1.5} dot={false} />
                      <Line type="monotone" dataKey="base" stroke={SCENARIO_COLOR.base} strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="bull" stroke={SCENARIO_COLOR.bull} strokeWidth={1.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* AI commentary */}
              {active.commentary && (
                <div className="nexus-card p-6" data-testid="finance-commentary">
                  <div className="label-eyebrow mb-2">// AI CFO Commentary</div>
                  <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{active.commentary}</div>
                </div>
              )}
            </>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500 text-sm">
              <Calculator className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1} />
              {active ? "This scenario hasn't been modeled. Re-run from the Run Scenario button." : "Select or run a scenario."}
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// New Scenario</div>
                <h2 className="font-display text-2xl text-white">Run Financial Model</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Scenario name *">
                <input data-testid="scn-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fn-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Starting monthly revenue ($)">
                  <input type="number" value={form.revenue_base} onChange={(e) => setForm({ ...form, revenue_base: +e.target.value })} className="fn-input" />
                </Field>
                <Field label="Starting monthly cost ($)">
                  <input type="number" value={form.cost_base} onChange={(e) => setForm({ ...form, cost_base: +e.target.value })} className="fn-input" />
                </Field>
                <Field label="Annual growth %">
                  <input type="number" step="0.01" value={form.growth_pct} onChange={(e) => setForm({ ...form, growth_pct: +e.target.value })} className="fn-input" />
                </Field>
                <Field label="Horizon (months)">
                  <input type="number" value={form.horizon_months} onChange={(e) => setForm({ ...form, horizon_months: +e.target.value })} className="fn-input" />
                </Field>
              </div>
              <Field label="Notes (optional)">
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="fn-input resize-none" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button
                onClick={run}
                disabled={busy}
                data-testid="run-scenario-btn"
                className="px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                {busy ? "Modeling…" : "Run"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <style>{`.fn-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .fn-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}
const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
